import { MarkElementProps } from "@mui/x-charts";
import { useInteractionItemProps } from "@mui/x-charts/internals";
import { Fragment } from "react";

import { INITIAL_YEAR, ReductionProposal } from "legacyNext/types";

type Props = MarkElementProps & {
    proposals: Array<Pick<ReductionProposal, "id" | "startYear">>;
};

/**
 * Custom mark component for the chart.
 * Partial logic from the MarkElement component
 */
export const ChartMark = (props: Props) => {
    const { id, dataIndex, proposals } = props;
    const getInteractionItemProps = useInteractionItemProps();

    const d = 13;

    const { x, y } = props as { x: number; y: number };
    const shapeProps = {
        cx: d,
        cy: d,
        r: d,
        fill: "#3579F6",
        stroke: "#d2ceff",
        strokeWidth: 2,
    };

    const year = INITIAL_YEAR + props.dataIndex;
    const enabled = proposals.filter((p) => p.startYear === year);

    const circlesTransform = enabled
        .map((p, index) => {
            const xTransform = index * (d * 1.55);
            const transform = `translate(${x - d + xTransform}, ${y - d})`;

            return {
                id: p.id,
                transform,
                x: x + xTransform,
                y,
            };
        })
        .reverse();

    if (!enabled.length) {
        return (
            <polygon
                id={`mark-${id}-${dataIndex}`}
                fill="#3579F6"
                stroke="#3579F6"
                points="6 1 11 6 6 11 1 6 6 1"
                transform={`translate(${x - 6}, ${y - 6})`}
            />
        );
    }

    const tooltipArea = {
        x: x - d - 1,
        y: y - d - 1,
        width: d * 2 * circlesTransform.length + 2,
        height: d * 2 + 2,
    };

    const interactionProps = getInteractionItemProps({
        type: "line",
        seriesId: id,
        dataIndex,
    }) as any;

    return (
        <g id={`mark-${id}-${dataIndex}`}>
            {circlesTransform.map((p) => (
                <Fragment key={p.id}>
                    <circle
                        key={p.id}
                        transform={p.transform}
                        {...shapeProps}
                    />
                    <text
                        x={p.x}
                        y={p.y + 1.4}
                        textAnchor="middle"
                        dominantBaseline="middle"
                        fontSize="14"
                        fontWeight="bold"
                        fill="white"
                        style={{ userSelect: "none" }}
                    >
                        {p.id}
                    </text>
                </Fragment>
            ))}
            <rect
                x={tooltipArea.x}
                y={tooltipArea.y}
                width={tooltipArea.width}
                height={tooltipArea.height}
                fill="transparent"
                style={{ userSelect: "none" }}
                {...interactionProps}
            />
        </g>
    );
};
